import { useState, Fragment } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  IconButton,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
  Menu,
  MenuItem,
  Link,
  Box,
  Typography,
  Card,
  useMediaQuery,
  useTheme,
  Rating,
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';

// Custom Components
import { PropertyClientsDrawer } from '../..';

// Utils
import { formatPhone } from '../../../utils/formatter';
// cleaners removed — minimal payloads used instead to avoid validation issues
import messages from '../../../static/messages';

// Services
import propertiesServices from '../../../services/propertiesServices';
import clientsServices from '../../../services/clientsServices';

// Hooks
import usePagination from '../../../hooks/usePagination';
import useDrawer from '../../../hooks/useDrawer';
import { useAuth } from '../../../hooks/useAuth';

// Styles
import { actions_column, table_container_base } from '../../../static/styles';
import { button } from '../../../static/theme-styles';

const menuPaperProps = {
  elevation: 0,
  sx: {
    left: '105px !important',
    overflow: 'visible',
    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
    mt: 1.5,
    '& .MuiSvgIcon-root': { width: 25, height: 25, mr: 1 },
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      left: 14,
      width: 10,
      height: 10,
      bgcolor: 'background.paper',
      transform: 'translateY(-50%) rotate(45deg)',
      zIndex: 0,
    },
  },
};

export const base_client = {
  client: {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  },
  relation: '',
};

// Mobile Client Card
const MobileClientCard = ({ client, onEdit, onRemove, onCall, onEmail, canDelete }) => {
  const navigate = useNavigate();
  
  if (!client.client || !client.client.id) return null;

  return (
    <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
      <Box 
        onClick={() => navigate(`/clients/${client.client.id}`)}
        sx={{ cursor: 'pointer' }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ fontSize: 20, color: '#1976d2', mr: 1 }} />
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              {client.client.fullName || `${client.client.firstName || ""} ${client.client.lastName || ""}`}
            </Typography>
            {client.client.rating > 0 && (
              <Rating value={client.client.rating} size="small" readOnly sx={{ ml: 1 }} />
            )}
          </Box>
          <ChevronRightIcon sx={{ color: '#ccc' }} />
        </Box>

        {client.relation && (
          <Typography variant="caption" sx={{ 
            color: '#666', 
            backgroundColor: '#f5f5f5', 
            px: 1, 
            py: 0.25, 
            borderRadius: 1,
            display: 'inline-block',
            mb: 1,
          }}>
            {client.relation}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        {client.client.phoneNumber && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<PhoneIcon />}
            onClick={(e) => { e.stopPropagation(); onCall(client.client.phoneNumber); }}
            sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontSize: 12 }}
          >
            {formatPhone(client.client.phoneNumber)}
          </Button>
        )}
        {client.client.email && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EmailIcon />}
            onClick={(e) => { e.stopPropagation(); onEmail(client.client.email); }}
            sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontSize: 12 }}
          >
            Email
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          variant="text"
          size="small"
          startIcon={<EditIcon />}
          onClick={(e) => { e.stopPropagation(); onEdit(client.client.id); }}
          sx={{ flex: 1, textTransform: 'none' }}
        >
          Edit
        </Button>
        {canDelete && (
          <Button
            variant="text"
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={(e) => { e.stopPropagation(); onRemove(client.client.id); }}
            sx={{ flex: 1, textTransform: 'none' }}
          >
            Remove
          </Button>
        )}
      </Box>
    </Box>
  );
};

const PropertyClientsTable = ({ clientsList, id, refresh }) => {
  const [currentClient, setCurrentClient] = useState();
  const [drawerMode, setDrawerMode] = useState('find');
  const [anchorEl, setAnchorEl] = useState(null);
  const openMore = Boolean(anchorEl);

  const confirm = useConfirm();
  const { drawer, openDrawer, closeDrawer, foolCheck } = useDrawer(currentClient);
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userData } = useAuth();
  
  // Check if user can delete clients (superadmin or admin only)
  const canDeleteClient = userData?.role === 'superadmin' || userData?.role === 'admin';

  // Action functions
  const onCloseDrawer = async (resetErrors) => {
    const confirmed = await foolCheck();
    if (confirmed === true) {
      setDrawerMode('find');
      setCurrentClient(null);
      resetErrors && resetErrors();
    }
  };

  const closeDrawerAfterSave = () => {
    closeDrawer();
    setDrawerMode('find');
    setCurrentClient(null);
  };

  const onAddClient = () => {
    setCurrentClient(base_client);
    setDrawerMode('new');
    openDrawer();
  };

  const onFindClient = () => {
    setCurrentClient(base_client);
    setDrawerMode('find');
    openDrawer();
  };

  const onEditClient = (id) => {
    const client = clientsList.find((el) => el.client?.id === id);
    if (!client) return;
    delete client.createdAt;
    delete client.deletedAt;
    delete client.updatedAt;
    setCurrentClient(client);
    setDrawerMode('edit');
    openDrawer();
  };

  const handleClick = (event) => setAnchorEl(event.currentTarget);

  const handleCall = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const handleEmail = (email) => {
    window.location.href = `mailto:${email}`;
  };

  // Async functions
  const addNewClient = async () => {
    if (!id) {
      toast.error('Property ID is missing');
      return;
    }

    // Guard: the selected client is already linked to this property
    const newClientId = currentClient?.client?.id;
    if (newClientId && clientsList.some(c => c.client?.id === newClientId)) {
      const linked = clientsList.find(c => c.client?.id === newClientId);
      const name = [linked.client.firstName, linked.client.lastName].filter(Boolean).join(' ') || 'This client';
      toast.info(`${name} is already linked to this property${linked.relation ? ` as "${linked.relation}"` : ''}.`);
      closeDrawerAfterSave();
      return;
    }
    
    // Build minimal payload for existing clients (only id + relation + client.id)
    // to avoid validation errors on legacy data (e.g. empty email strings)
    const existingClients = clientsList
      .filter(c => c.client?.id)
      .map(c => ({
        id: c.id,
        relation: c.relation || '',
        client: { id: c.client.id }
      }));

    // Build new client entry
    const newClientData = { ...currentClient.client };
    // Remove empty strings that would fail email/format validation
    Object.keys(newClientData).forEach(key => {
      if (newClientData[key] === '' || newClientData[key] === null) {
        delete newClientData[key];
      }
    });
    const newClient = { client: newClientData, relation: currentClient.relation || '' };
    const updatedClients = { clients: [...existingClients, newClient] };

    try {
      const response = await propertiesServices.update(id, updatedClients);
      if (response.status === 200) {
        closeDrawerAfterSave();
        toast.success(messages.property.update);
        refresh();
      }
    } catch (error) {
      const msg = error?.response?.data?.message || messages.errors.error_try_again;
      toast.error(msg);
      console.log(error);
    }
  };

  const updateClient = async () => {
    delete currentClient.isEdited;

    try {
      // Update client profile
      let clientUpdated = false;
      if (currentClient.client?.id) {
        const clientData = {};

        // firstName/phoneNumber are effectively required and email cannot be
        // cleared (the API rejects null/empty for its format check), so these
        // are only sent when non-empty; the rest are nulled when cleared.
        ['firstName', 'phoneNumber', 'email'].forEach((key) => {
          if (currentClient.client[key]) clientData[key] = currentClient.client[key];
        });
        ['lastName', 'companyName', 'mobile', 'fax'].forEach((key) => {
          if (currentClient.client[key] !== undefined) {
            clientData[key] = currentClient.client[key] || null;
          }
        });

        if (Object.keys(clientData).length > 0) {
          await clientsServices.update(currentClient.client.id, clientData);
          clientUpdated = true;
        }
      }

      // Check if relation changed - only then update property
      const originalClient = clientsList.find(c => c.client?.id === currentClient.client?.id);
      const relationChanged = originalClient && originalClient.relation !== currentClient.relation;

      if (relationChanged) {
        // Update property-client relation (only if relation changed)
        const updatedRelation = {
          clients: clientsList.map(c => {
            if (c.client?.id === currentClient.client?.id) {
              return {
                id: c.id,
                relation: currentClient.relation || '',
                client: { id: c.client.id }
              };
            }
            return {
              id: c.id,
              relation: c.relation || '',
              client: { id: c.client?.id }
            };
          }).filter(c => c.client?.id)
        };

        await propertiesServices.update(id, updatedRelation);
      }

      closeDrawerAfterSave();
      toast.success('Client updated successfully');
      refresh();
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const removeClientFromProperty = async (clientId) => {
    try {
      await confirm({
        title: 'Remove Client from Property',
        description: 'Are you sure you want to remove this client from the property? This will only unlink the client, not delete the client record.',
        confirmationText: 'Remove',
        confirmationButtonProps: { color: 'error' },
      });

      // Filter out the client to be removed
      const updatedClients = {
        clients: clientsList
          .filter(c => c.client?.id !== clientId)
          .map(c => ({
            id: c.id,
            relation: c.relation || '',
            client: { id: c.client?.id }
          }))
          .filter(c => c.client?.id)
      };

      const response = await propertiesServices.update(id, updatedClients);
      if (response.status === 200) {
        toast.success('Client removed from property');
        refresh();
      }
    } catch (error) {
      // User cancelled or error occurred
      if (error !== undefined) {
        toast.error(messages.errors.error_try_again);
        console.log(error);
      }
    }
  };

  // ===================== MOBILE VIEW =====================
  if (isMobile) {
    return (
      <Fragment>
        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
          <Button 
            variant="contained" 
            onClick={handleClick} 
            endIcon={<AddIcon />} 
            fullWidth
            sx={{ borderRadius: 2 }}
          >
            Add Client
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={openMore}
            onClose={() => setAnchorEl(null)}
            onClick={() => setAnchorEl(null)}
            PaperProps={menuPaperProps}
          >
            <MenuItem onClick={onFindClient}>
              <PersonSearchIcon /> Existing Client
            </MenuItem>
            <MenuItem onClick={onAddClient}>
              <PersonAddIcon /> New Client
            </MenuItem>
          </Menu>
        </Box>

        {clientsList.length ? (
          clientsList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((client) => (
            <MobileClientCard
              key={client.client?.id}
              client={client}
              onEdit={onEditClient}
              onRemove={removeClientFromProperty}
              onCall={handleCall}
              onEmail={handleEmail}
              canDelete={canDeleteClient}
            />
          ))
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#999' }}>
              No clients linked to this property
            </Typography>
          </Box>
        )}

        {clientsList.length > 5 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={clientsList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}

        <PropertyClientsDrawer
          drawer={drawer}
          onCloseDrawer={onCloseDrawer}
          drawerMode={drawerMode}
          currentClient={currentClient}
          setCurrentClient={setCurrentClient}
          addNewClient={addNewClient}
          updateClient={updateClient}
        />
      </Fragment>
    );
  }

  // ===================== DESKTOP VIEW (unchanged) =====================
  return (
    <Fragment>
      <Button variant="contained" onClick={handleClick} endIcon={<AddIcon />} sx={{ ...button('secondary'), mb: 2 }}>
        Add Client
      </Button>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={openMore}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
        PaperProps={menuPaperProps}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={onFindClient}>
          <PersonSearchIcon /> Existing Client
        </MenuItem>
        <MenuItem onClick={onAddClient}>
          <PersonAddIcon /> New Client
        </MenuItem>
      </Menu>

      <TableContainer component={Paper} sx={table_container_base}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width="25%">Name</TableCell>
              <TableCell width="20%">Phone</TableCell>
              <TableCell width="25%">Email</TableCell>
              <TableCell width="25%">Relation to Property</TableCell>
              <TableCell width="5%">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clientsList.length ? (
              clientsList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((client) => {
                if (!client.client || !client.client.id) return null;
                return (
                  <TableRow key={client.client.id}>
                    <TableCell scope="row">
                      <Link className="full-col-flex-link" href={`/clients/${client.client.id}`} underline="none" color="rgba(0, 0, 0, 0.87)" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {client.client.fullName || `${client.client.firstName || ""} ${client.client.lastName || ""}`}
                        {client.client.rating > 0 && (
                          <Rating value={client.client.rating} size="small" readOnly />
                        )}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link className="full-col-flex-link" href={`/clients/${client.client.id}`} underline="none" color="rgba(0, 0, 0, 0.87)">
                        {formatPhone(client.client.phoneNumber)}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link className="full-col-flex-link" href={`/clients/${client.client.id}`} underline="none" color="rgba(0, 0, 0, 0.87)">
                        {client.client.email}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link className="full-col-flex-link" href={`/clients/${client.client.id}`} underline="none" color="rgba(0, 0, 0, 0.87)">
                        {client.relation}
                      </Link>
                    </TableCell>
                    <TableCell sx={actions_column}>
                      <Tooltip title="Edit Client">
                        <IconButton aria-label="edit" onClick={() => onEditClient(client.client.id)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {canDeleteClient && (
                        <Tooltip title="Remove from Property">
                          <IconButton 
                            aria-label="remove" 
                            onClick={() => removeClientFromProperty(client.client.id)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center' }}>
                  <strong>No Data</strong>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={clientsList.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Clients per page"
      />

      <PropertyClientsDrawer
        drawer={drawer}
        onCloseDrawer={onCloseDrawer}
        drawerMode={drawerMode}
        currentClient={currentClient}
        setCurrentClient={setCurrentClient}
        addNewClient={addNewClient}
        updateClient={updateClient}
      />
    </Fragment>
  );
};

export default PropertyClientsTable;
